import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { PropTypes } from "prop-types";
import { Nav } from "reactstrap";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import PerfectScrollbar from "perfect-scrollbar";

var ps;

function Sidebar(props) {
  const location = useLocation();
  const sidebarRef = React.useRef(null);

  // Determinar qué submenús deben estar abiertos por defecto
  // (los que contienen la ruta activa)
  const getInitialOpenMenus = () => {
    const open = {};
    props.routes.forEach((route, key) => {
      if (route.subRoutes) {
        const isActive = route.subRoutes.some((sub) =>
          location.pathname.startsWith(route.layout + route.path + sub.path)
        );
        if (isActive) open[key] = true;
      }
    });
    return open;
  };

  const [openMenus, setOpenMenus] = useState(getInitialOpenMenus);

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(sidebarRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
      }
    };
  }, []);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isRouteActive = (path) => location.pathname.startsWith(path);

  const renderRoutes = (routes, parentPath = "") => {
    return routes.map((prop, key) => {
      if (prop.redirect || prop.hide) return null;

      const categoryLabel = prop.category ? (
        <li
          key={`cat-${key}`}
          style={{
            padding: "16px 15px 4px 28px",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.35)",
            listStyle: "none",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {prop.category}
        </li>
      ) : null;

      if (prop.subRoutes) {
        const fullPath = prop.layout + prop.path;
        const isParentActive = prop.subRoutes.some((sub) =>
          isRouteActive(fullPath + sub.path)
        );
        const isOpen = openMenus[key] ?? isParentActive;

        return (
          <React.Fragment key={key}>
            {categoryLabel}
            <li className={isParentActive ? "active" : ""}>
              <a
                href="#/"
                className="nav-link"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  toggleMenu(key);
                }}
              >
                <i className={prop.icon} />
                <p>
                  {prop.name}
                  <b
                    className="caret"
                    style={{
                      display: "inline-block",
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: isOpen
                        ? "translateY(-50%) rotate(180deg)"
                        : "translateY(-50%) rotate(0deg)",
                      transition: "transform 0.2s ease",
                      borderTop: "4px solid",
                      borderRight: "4px solid transparent",
                      borderLeft: "4px solid transparent",
                      borderBottom: "none",
                      width: 0,
                      height: 0,
                    }}
                  />
                </p>
              </a>

              {/* Submenu con animación */}
              <div
                style={{
                  maxHeight: isOpen ? "500px" : "0",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease",
                }}
              >
                <ul className="nav" style={{ marginTop: 0 }}>
                  {prop.subRoutes.map((sub, subKey) => {
                    const subFullPath = prop.layout + prop.path + sub.path;
                    const isSubActive = isRouteActive(subFullPath);
                    return (
                      <li key={subKey} className={isSubActive ? "active" : ""}>
                        <NavLink
                          to={subFullPath}
                          className="nav-link"
                          onClick={props.toggleSidebar}
                          style={{ paddingLeft: "46px" }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: isSubActive
                                ? "white"
                                : "rgba(255,255,255,0.4)",
                              marginRight: "10px",
                              flexShrink: 0,
                              verticalAlign: "middle",
                            }}
                          />
                          <p style={{ display: "inline", verticalAlign: "middle", fontSize: "13px" }}>
                            {sub.name}
                          </p>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </li>
          </React.Fragment>
        );
      }

      // Ruta simple
      const fullPath = prop.layout + prop.path;
      const isActive = isRouteActive(fullPath);
      return (
        <React.Fragment key={key}>
          {categoryLabel}
          <li className={isActive ? "active" : ""}>
            <NavLink
              to={fullPath}
              className="nav-link"
              onClick={props.toggleSidebar}
            >
              <i className={prop.icon} />
              <p>{prop.name}</p>
            </NavLink>
          </li>
        </React.Fragment>
      );
    });
  };

  const { routes, logo } = props;

  let logoImg = null;
  let logoText = null;

  if (logo !== undefined) {
    if (logo.outterLink !== undefined) {
      logoImg = (
        <a
          href={logo.outterLink}
          className="simple-text logo-mini"
          target="_blank"
          rel="noopener noreferrer"
          onClick={props.toggleSidebar}
        >
          <div className="logo-img">
            <img src={logo.imgSrc} alt="falcons-logo" />
          </div>
        </a>
      );
      logoText = (
        <a
          href={logo.outterLink}
          className="simple-text logo-normal"
          target="_blank"
          rel="noopener noreferrer"
          onClick={props.toggleSidebar}
        >
          {logo.text}
        </a>
      );
    } else {
      logoImg = (
        <Link
          to={logo.innerLink}
          className="simple-text logo-mini"
          onClick={props.toggleSidebar}
        >
          <div className="logo-img">
            <img src={logo.imgSrc} alt="falcons-logo" />
          </div>
        </Link>
      );
      logoText = (
        <Link
          to={logo.innerLink}
          className="simple-text logo-normal"
          onClick={props.toggleSidebar}
        >
          {logo.text}
        </Link>
      );
    }
  }

  return (
    <BackgroundColorContext.Consumer>
      {({ color }) => (
        <div className="sidebar" data={color}>
          <div className="sidebar-wrapper" ref={sidebarRef}>
            {(logoImg !== null || logoText !== null) && (
              <div className="logo">
                {logoImg}
                {logoText}
              </div>
            )}
            <Nav>{renderRoutes(routes)}</Nav>
          </div>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
}

Sidebar.propTypes = {
  rtlActive: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    text: PropTypes.node,
    imgSrc: PropTypes.string,
  }),
  toggleSidebar: PropTypes.func,
};

export default Sidebar;
